import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const BACKEND_URL = environment.apiUrl + "/ink/";
const SUPPLIER_ORDERS_URL = environment.apiUrl + "/api/supplier-orders/";
const EXPRESS_PAPERS_URL = environment.apiUrl + "/papers/express-papers";
const HELPERS_DISTRIBUTION_LIST_URL = environment.apiUrl + "/api/helpers-distribution-list/";
const PRINT_QUEUE_MESSAGE_URL = environment.apiUrl + "/api/print-queue-message/";
const USER_SMS_URL = environment.apiUrl + "/api/user-sms/";

@Injectable({
  providedIn: 'root'
})
export class GeneralManagementService {

  constructor(private http: HttpClient) { }

  /**
   * Get all inks from the backend
   * @returns Observable of inks array
   */
  getAllInks(): Observable<any[]> {
    console.log('Making request to:', BACKEND_URL);
    
    // Get printing service and branch from localStorage (same as AuthInterceptor)
    const printingService = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    
    // Add parameters to URL for GET request
    let url = BACKEND_URL;
    if (printingService && printingService !== "" && printingService !== null) {
      url += `?printingservice=${printingService}`;
    }
    if (branch && branch !== "" && branch !== null) {
      url += printingService ? `&branch=${branch}` : `?branch=${branch}`;
    }
    
    console.log('Final URL with params:', url);
    
    return this.http
      .get<{ message: string; inks: any[]; count: number }>(url)
      .pipe(
        map(inkData => {
          console.log('Raw response from backend:', inkData);
          console.log('Inks array:', inkData.inks);
          console.log('Count:', inkData.count);
          return [...inkData.inks];
        }),
        catchError(error => {
          console.error('Error fetching inks:', error);
          throw error;
        })
      );
  }

  /**
   * Create a new supplier order
   * @param orderData - The order data to submit
   * @returns Observable of the created order
   */
  createSupplierOrder(orderData: any): Observable<any> {
    return this.http
      .post<{ message: string; order: any }>(SUPPLIER_ORDERS_URL, orderData)
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => {
          console.error('Error creating supplier order:', error);
          throw error;
        })
      );
  }

  /**
   * Delete a supplier order
   * @param orderId - The ID of the order to delete
   * @param orderData - The order data needed for deletion (to reverse inventory if order was received)
   * @returns Observable of the deletion response
   */
  deleteSupplierOrder(orderId: string, orderData: any): Observable<any> {
    console.log('Deleting supplier order:', orderId);
    console.log('Order data:', orderData);
    
    return this.http
      .request<{ message: string }>('DELETE', `${SUPPLIER_ORDERS_URL}${orderId}`, { body: orderData })
      .pipe(
        map(response => {
          console.log('Order deleted successfully:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error deleting supplier order:', error);
          throw error;
        })
      );
  }

  /**
   * Get supplier orders with pagination (ALL branches)
   * @param page - Current page number
   * @param pageSize - Number of orders per page
   * @param statusFilter - Array of statuses to filter by: 'regular', 'manual_update', 'auto_replacement'
   * @returns Observable of supplier orders data
   */
  getSupplierOrders(page: number = 1, pageSize: number = 10, statusFilter: string[] = []): Observable<any> {
    let url = `${SUPPLIER_ORDERS_URL}?page=${page}&pagesize=${pageSize}`;
    
    // Add status filter parameters
    if (statusFilter && statusFilter.length > 0) {
      statusFilter.forEach(status => {
        url += `&status=${status}`;
      });
    }
    
    console.log('Fetching supplier orders from:', url);
    
    return this.http
      .get<{ orders: any[]; maxOrders: number }>(url)
      .pipe(
        map(response => {
          console.log('Supplier orders fetched:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error fetching supplier orders:', error);
          throw error;
        })
      );
  }

  /**
   * Get supplier orders with pagination filtered by branch and printing service
   * @param branchId - Current branch ID
   * @param printingService - Current printing service
   * @param page - Current page number
   * @param pageSize - Number of orders per page
   * @param statusFilter - Array of statuses to filter by: 'regular', 'manual_update', 'auto_replacement'
   * @returns Observable of supplier orders data
   */
  getSupplierOrdersByBranch(branchId: string, printingService: string, page: number = 1, pageSize: number = 10, statusFilter: string[] = []): Observable<any> {
    let url = `${SUPPLIER_ORDERS_URL}?branchId=${branchId}&printingService=${printingService}&page=${page}&pagesize=${pageSize}`;
    
    // Add status filter parameters
    if (statusFilter && statusFilter.length > 0) {
      statusFilter.forEach(status => {
        url += `&status=${status}`;
      });
    }
    
    console.log('Fetching supplier orders by branch from:', url);
    
    return this.http
      .get<{ orders: any[]; maxOrders: number }>(url)
      .pipe(
        map(response => {
          console.log('Supplier orders fetched by branch:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error fetching supplier orders by branch:', error);
          throw error;
        })
      );
  }

  /**
   * Update default settings values
   * @param updateData - Object containing branchId, printingService, isPicsPrinter, and the values to update
   * @returns Observable of the updated data
   */
  updateDefaultSettings(updateData: {
    branchId: string;
    printingService: string;
    isPicsPrinter: boolean;
    updatedValues: any;
    applyToAll?: boolean;
    category?: string; // 'paper' or 'ink'
    itemKey?: string; // Specific item being updated
  }): Observable<any> {
    const url = `${BACKEND_URL}default-settings`; // Backend expects /ink/default-settings
    console.log('Updating default settings to:', url);
    console.log('Update data:', updateData);
    
    return this.http
      .put<{ message: string; updatedData: any }>(url, updateData)
      .pipe(
        map(response => {
          console.log('Default settings updated successfully:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error updating default settings:', error);
          throw error;
        })
      );
  }

  /**
   * Mark supplier order as arrived and update branch/printer stock
   * @param updateData - Object containing orderId, branchId, printingService, orderQuantities, etc.
   * @returns Observable of the updated data
   */
  markOrderAsArrived(updateData: {
    orderId: string;
    branchId: string;
    printingService: string;
    isPicsPrinter: boolean;
    orderQuantities: any;
    orderSummaryItems?: any[];
  }): Observable<any> {
    const url = `${SUPPLIER_ORDERS_URL}mark-arrived`;
    console.log('Marking order as arrived to:', url);
    console.log('Update data:', updateData);
    
    return this.http
      .put<{ message: string; order: any }>(url, updateData)
      .pipe(
        map(response => {
          console.log('Order marked as arrived successfully:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error marking order as arrived:', error);
          throw error;
        })
      );
  }

  /**
   * Get all express papers from the backend
   * @returns Observable of express papers array
   */
  getAllExpressPapers(): Observable<any[]> {
    console.log('CHECK_EXPRESS_PAPERS [getAllExpressPapers] - Making request to:', EXPRESS_PAPERS_URL);
    
    return this.http
      .get<{ message: string; papers: any[]; count: number }>(EXPRESS_PAPERS_URL)
      .pipe(
        map(paperData => {
          console.log('CHECK_EXPRESS_PAPERS [getAllExpressPapers] - Raw response from backend:', JSON.stringify(paperData));
          console.log('Express papers array:', paperData.papers);
          console.log('Count:', paperData.count);
          return [...paperData.papers];
        }),
        catchError(error => {
          console.error('CHECK_EXPRESS_PAPERS [getAllExpressPapers] - Error fetching express papers:', error);
          console.error('Error details:', JSON.stringify(error));
          throw error;
        })
      );
  }

  /**
   * Join helpers distribution list
   * @param printingService - 'express' or 'plotter'
   * @param branchOrPrinterId - Branch ID (for plotter) or Printer ID (for express)
   * @param userId - User ID
   * @param phone - Phone number (without leading 0, as number)
   * @returns Observable of the response
   */
  joinHelpersDistributionList(
    printingService: string,
    branchOrPrinterId: string,
    userId: string,
    phone: number
  ): Observable<any> {
    const requestData = {
      printingService: printingService,
      branchOrPrinterId: branchOrPrinterId,
      userId: userId,
      phone: phone
    };
    
    return this.http
      .post<{ message: string; success: boolean }>(HELPERS_DISTRIBUTION_LIST_URL + 'join', requestData)
      .pipe(
        map(response => {
          console.log('Join helpers distribution list successful:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error joining helpers distribution list:', error);
          console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
          throw error;
        })
      );
  }

  /**
   * Send message to branch helpers
   * @param printingService - 'express' or 'plotter'
   * @param branchOrPrinterId - Branch ID (for plotter) or Printer ID (for express)
   * @param messageContent - The message content to send
   * @returns Observable of the response
   */
  sendMessageToHelpers(
    printingService: string,
    branchOrPrinterId: string,
    messageContent: string
  ): Observable<any> {
    const requestData = {
      printingService: printingService,
      branchOrPrinterId: branchOrPrinterId,
      messageContent: messageContent
    };
    
    return this.http
      .post<{ message: string; success: boolean }>(HELPERS_DISTRIBUTION_LIST_URL + 'send-message', requestData)
      .pipe(
        map(response => {
          console.log('Send message to helpers successful:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error sending message to helpers:', error);
          console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
          throw error;
        })
      );
  }

  /**
   * Send message to users in print queue (plotter only).
   * plotterId = branch.serial_name (e.g. "hadarion", "technion"), like other queue APIs.
   */
  /**
   * שליחת SMS למשתמש לפי מייל (מנהלים) — ראוט בקאנד: POST /api/user-sms/send
   */
  sendSmsToUser(payload: {
    targetUserEmail: string;
    messageContent: string;
    printingService?: string;
    branchId?: string;
  }): Observable<any> {
    return this.http
      .post<{ message?: string; success?: boolean }>(USER_SMS_URL + 'send', payload)
      .pipe(
        map((response) => {
          console.log('Send SMS to user successful:', response);
          return response;
        }),
        catchError((error) => {
          console.error('Error sending SMS to user:', error);
          throw error;
        })
      );
  }

  sendMessageToPrintQueue(
    plotterId: string,
    messageContent: string
  ): Observable<any> {
    const requestData = {
      plotterId,
      messageContent
    };
    
    return this.http
      .post<{ message: string; success: boolean }>(PRINT_QUEUE_MESSAGE_URL + 'send', requestData)
      .pipe(
        map(response => {
          console.log('Send message to print queue successful:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error sending message to print queue:', error);
          console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
          throw error;
        })
      );
  }

  /**
   * Remove user from helpers distribution list
   * Backend will search all clusters and remove the user from all helpers groups
   * @param userId - User ID (from query param 'x')
   * @returns Observable of the response
   */
  removeFromHelpersDistributionList(
    userId: string
  ): Observable<any> {
    const requestData = {
      userId: userId
    };
    
    return this.http
      .post<{ message: string; success: boolean }>(HELPERS_DISTRIBUTION_LIST_URL + 'remove', requestData)
      .pipe(
        map(response => {
          console.log('Remove from helpers distribution list successful:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error removing from helpers distribution list:', error);
          console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
          throw error;
        })
      );
  }
}
